/**
 * 上传前图片归一化
 * -----------------------------------------------------------------------------
 * 为什么必须在**上传前**做：证件照要送两家人脸/人体服务，其中人体分割（`body_seg`）
 * 对入参有硬限制 —— 体积与长边都有上限，超了直接返回 `216202 image size error`。
 *
 * 2026-10-27 真机暴露：同一账号换一台手机拍证件照就报「服务异常」。
 * 服务端日志给出的原话是 `人像分割返回错误 error_code=216202 msg=image size error`。
 * 原因不在服务端：实时取景那条路走 canvas 出图（尺寸天然可控），而**相册 / 系统相机
 * 那条路把手机给的原始文件直接传上去**了 —— 一亿像素级的原图动辄 8~12MB、长边 8000+，
 * 必然被拒。换手机 = 换相机 = 换照片规格，所以「只在那台手机上坏」。
 *
 * 因此：**任何来源的照片都先过这里**，把「设备差异」在进网络之前抹平。
 *
 * 目标不只是「不被拒」，还有用户明确提的（2026-10-27）：「相机拍的图普遍太大，
 * 适当压缩，提高速度、节约带宽」。10MB 原图在移动网络上传一次要好几秒，
 * 而一寸照成品才 295×413 —— 源图留 1800 长边已经富余 4 倍以上，再大纯属浪费。
 * 所以这里是**双向压缩**：既压掉供应商拒收的风险，也压掉无意义的流量。
 * 具体数值与「为什么不能再小」见下方 `IMAGE_MAX_EDGE` 的注释。
 */

/**
 * 长边上限（px）。
 *
 * 定 1800 的算法（**不许再往下压，除非同步调低服务端头高下限**）：
 * 服务端证件照要求源图里**头高 ≥ 240px**（`ID_PHOTO_MIN_HEAD_PIXELS`），低于此值
 * 只能上采样，成片发虚且打印会糊。半身构图里头高约占画面高度的 1/6：
 * `1800 × 1/6 = 300px`，相对 240 留 25% 余量；压到 1440 就只有 240px，正好卡线，
 * 稍微构远一点就判重拍 —— 那是把「省流量」换成了「多拍一次」。
 *
 * 1800 也已经远超一寸照的实际需要：成品是 295×413（@300dpi），源图长边 1800
 * 意味着按 ≈4.4 倍下采样，画质有富余。
 */
export const IMAGE_MAX_EDGE = 1800;
/**
 * 体积上限（字节）。
 *
 * 用户口径（2026-10-27）：「相机拍的图普遍太大，适当压缩，提高速度、节约带宽」。
 * 手机原图动辄 5~12MB，压缩后典型 400~900KB，上传快一个数量级。
 *
 * 同时它必须**小于供应商的 3MB**（百度限 base64 后 4MB，base64 膨胀 4/3）——
 * 所以这个数不是纯体验参数，也是一个硬约束：改大就等于让 `216202` 有机会复现。
 */
export const IMAGE_MAX_BYTES = 1.2 * 1024 * 1024;
/** 首次编码质量。0.85 在 1800 长边下肉眼无差，比 0.92 再省约 30% 体积。 */
export const IMAGE_QUALITY = 0.85;

export interface NormalizeOptions {
  maxEdge?: number;
  maxBytes?: number;
  quality?: number;
}

/** 照片读不出来（HEIC 等浏览器不解的格式、文件损坏）。文案直接面向学员。 */
export class ImageNormalizeError extends Error {
  constructor(message = '这张照片无法读取，请重新拍摄或换一张') {
    super(message);
    this.name = 'ImageNormalizeError';
  }
}

/**
 * 等比缩放到长边不超过 `maxEdge`。
 *
 * **不放大**：小图原样返回，放大只会让体积变大、画质更虚。
 * 纯函数（不碰 DOM），单独可测 —— 尺寸算错会让证件照比例失真，比崩溃更难发现。
 */
export function computeScaledSize(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (!Number.isFinite(longEdge) || longEdge <= 0) return { width, height };
  if (longEdge <= maxEdge) return { width, height };
  const ratio = maxEdge / longEdge;
  // `Math.round` 而不是 `floor`：两边都向下取会累积成肉眼可见的形变
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageNormalizeError());
    };
    image.src = url;
  });
}

function encode(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new ImageNormalizeError('照片处理失败，请重新拍摄或换一张'));
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * 把任意来源的图片压成「供应商能收」的 JPEG。
 *
 * 三种情况直接原样返回，避免无意义的重编码（重编码一次就是一次画质损失）：
 * 体积已达标 **且** 长边不超限 **且** 本来就是 JPEG。
 * 其余统一走 canvas → JPEG。压完仍超标时先降质量、再降长边，最多三轮。
 */
export async function normalizeImage(input: Blob, options: NormalizeOptions = {}): Promise<Blob> {
  const maxEdge = options.maxEdge ?? IMAGE_MAX_EDGE;
  const maxBytes = options.maxBytes ?? IMAGE_MAX_BYTES;
  const quality = options.quality ?? IMAGE_QUALITY;

  const image = await loadImage(input);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (!width || !height) throw new ImageNormalizeError();

  const size = computeScaledSize(width, height, maxEdge);
  const untouched =
    size.width === width &&
    size.height === height &&
    input.size <= maxBytes &&
    input.type === 'image/jpeg';
  if (untouched) return input;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new ImageNormalizeError('当前浏览器不支持图片处理，请换一台手机重试');

  let currentEdge = maxEdge;
  let currentQuality = quality;
  let blob: Blob | null = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const target = computeScaledSize(width, height, currentEdge);
    canvas.width = target.width;
    canvas.height = target.height;
    // 白底：JPEG 没有透明通道，PNG 直接画上去会把透明区变成黑色
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, target.width, target.height);
    context.drawImage(image, 0, 0, target.width, target.height);
    blob = await encode(canvas, currentQuality);
    if (blob.size <= maxBytes) return blob;
    // 先降质量（保分辨率），仍超标再降长边
    if (currentQuality > 0.6) currentQuality -= 0.15;
    else currentEdge = Math.round(currentEdge * 0.75);
  }

  // 三轮还超标：与其让供应商回 `216202` 变成「服务异常」，不如如实告诉学员
  throw new ImageNormalizeError('照片太大，处理后仍超出限制，请重新拍摄');
}
